import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { X, Plus, Trash2 } from "lucide-react";

const createServiceTypeSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(1, "Service name is required"),
  description: z.string().min(1, "Description is required"),
  category: z.enum(["Authority", "DOT", "Business Formation", "Closure", "Compliance"]),
  estimatedDays: z.string().optional().default(""),
  relatedServiceTypes: z.array(z.string()).default([]),
  credentialTypeIds: z.array(z.number()).default([]),
  isActive: z.boolean().default(true),
});

type FormData = z.infer<typeof createServiceTypeSchema>;

interface CredentialType {
  id: number;
  name: string;
  agency: string | null;
  isActive: boolean;
}

interface CreateServiceTypeModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: FormData) => void;
  isLoading: boolean;
  serviceTypes?: any[];
  credentialTypes?: CredentialType[];
}

export default function CreateServiceTypeModal({
  open,
  onClose,
  onSubmit,
  isLoading,
  serviceTypes = [],
  credentialTypes = []
}: CreateServiceTypeModalProps) {
  const [selectedRelatedServices, setSelectedRelatedServices] = useState<string[]>([]);
  const [selectedCredentialTypes, setSelectedCredentialTypes] = useState<number[]>([]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<z.input<typeof createServiceTypeSchema>, unknown, FormData>({
    resolver: zodResolver(createServiceTypeSchema),
    defaultValues: {
      name: "",
      description: "",
      category: "Authority",
      estimatedDays: "",
      relatedServiceTypes: [],
      isActive: true,
    },
  });

  const category = watch("category");
  const isActive = watch("isActive");

  const handleFormSubmit = (data: FormData) => {
    onSubmit({
      ...data,
      relatedServiceTypes: selectedRelatedServices,
      credentialTypeIds: selectedCredentialTypes,
    });
  };

  const handleClose = () => {
    reset();
    setSelectedRelatedServices([]);
    setSelectedCredentialTypes([]);
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Create Service</CardTitle>
            <Button variant="ghost" size="sm" onClick={handleClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Service Name</Label>
                <Input
                  id="name"
                  {...register("name")}
                  placeholder="e.g., BOC-3 Filing"
                />
                {errors.name && (
                  <p className="text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                {...register("description")}
                placeholder="Describe what this service accomplishes..."
                rows={3}
              />
              {errors.description && (
                <p className="text-sm text-red-600">{errors.description.message}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Select
                  value={category}
                  onValueChange={(value) => setValue("category", value as any)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Authority">Authority</SelectItem>
                    <SelectItem value="DOT">DOT</SelectItem>
                    <SelectItem value="Business Formation">Business Formation</SelectItem>
                    <SelectItem value="Closure">Closure</SelectItem>
                    <SelectItem value="Compliance">Compliance</SelectItem>
                  </SelectContent>
                </Select>
                {errors.category && (
                  <p className="text-sm text-red-600">{errors.category.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="estimatedDays">Estimated Time</Label>
                <Input
                  id="estimatedDays"
                  {...register("estimatedDays")}
                  placeholder="e.g., 5-7 days"
                />
                {errors.estimatedDays && (
                  <p className="text-sm text-red-600">{errors.estimatedDays.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="isActive">Active Status</Label>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="isActive"
                    checked={isActive}
                    onCheckedChange={(checked) => setValue("isActive", checked)}
                  />
                  <Label htmlFor="isActive" className="text-sm">
                    {isActive ? "Active" : "Inactive"}
                  </Label>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Credentials</Label>
                <span className="text-sm text-gray-500">Optional</span>
              </div>
              
              <div className="space-y-2">
                <p className="text-sm text-gray-600">
                  Select the credentials applicable for this service:
                </p>
                <div className="border rounded-md p-3 max-h-32 overflow-y-auto">
                  {credentialTypes.filter(ct => ct.isActive).length === 0 ? (
                    <p className="text-sm text-gray-500">No credential types available</p>
                  ) : (
                    credentialTypes
                      .filter(ct => ct.isActive)
                      .map((credentialType) => (
                        <div key={credentialType.id} className="flex items-center space-x-2 py-1">
                          <input
                            type="checkbox"
                            id={`credential-${credentialType.id}`}
                            checked={selectedCredentialTypes.includes(credentialType.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                const newSelected = [...selectedCredentialTypes, credentialType.id];
                                setSelectedCredentialTypes(newSelected);
                                setValue("credentialTypeIds", newSelected);
                              } else {
                                const newSelected = selectedCredentialTypes.filter(id => id !== credentialType.id);
                                setSelectedCredentialTypes(newSelected);
                                setValue("credentialTypeIds", newSelected);
                              }
                            }}
                            className="rounded border-gray-300"
                          />
                          <label 
                            htmlFor={`credential-${credentialType.id}`}
                            className="text-sm cursor-pointer flex-1"
                          >
                            <span className="font-medium">{credentialType.name}</span>
                            {credentialType.agency && (
                              <span className="text-gray-500 ml-2">({credentialType.agency})</span>
                            )}
                          </label>
                        </div>
                      ))
                  )}
                </div>
                {selectedCredentialTypes.length > 0 && (
                  <p className="text-sm text-blue-600">
                    {selectedCredentialTypes.length} credential type(s) selected
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Creating..." : "Create Service"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}